import { createNodeIndex } from './nodeIndex';
import type { Point2 } from './point';
import { shoelaceDouble } from './polyline';

/** Fewest vertices that can bound area. */
const MIN_RING_POINTS = 3;

/** Drop faces whose enclosed area is numerically nothing. */
const MIN_FACE_AREA = 1e-6;

/**
 * Two outgoing edges at a node whose directions differ by less than this are
 * not a unique turn — overlapping or coincident segments. The tracer refuses
 * to guess and excludes walks that would have to pick between them.
 */
const ANGLE_TIE_RAD = 1e-9;

const edgeKey = (a: number, b: number): string =>
  a < b ? `${a}:${b}` : `${b}:${a}`;

/** Rotate so the lexicographically smallest vertex is first. Winding is kept. */
const rotateToMinVertex = (ring: readonly Point2[]): Point2[] => {
  if (ring.length === 0) {
    return [];
  }
  let minIndex = 0;
  for (let index = 1; index < ring.length; index += 1) {
    const point = ring[index];
    const min = ring[minIndex];
    if (!point || !min) {
      continue;
    }
    if (point[0] < min[0] || (point[0] === min[0] && point[1] < min[1])) {
      minIndex = index;
    }
  }
  return [...ring.slice(minIndex), ...ring.slice(0, minIndex)];
};

/**
 * Closed CCW rings bounded by `polylines`, traced as faces of their planar
 * arrangement.
 *
 * Each consecutive pair of welded vertices becomes an undirected edge. Dangling
 * spurs (degree 1) are pruned so they cannot become zero-area spikes. At every
 * remaining node the walk takes the unique next edge in clockwise angular
 * order — the leftmost turn with the face on the left — so a T or K of three
 * or more cutting branches is a well-defined face boundary rather than a guess.
 *
 * A node whose outgoing angles are tied (overlapping segments) is blocked:
 * any walk that would turn there is dropped instead of emitting a polygon.
 * Open chains never close and are not emitted. The tracer never invents a
 * closing chord, which is what kept a linear chain-walk from cutting across
 * empty material.
 *
 * Clockwise cycles (the unbounded face, and the reverse of every bounded one)
 * are discarded. Callers nest the CCW rings even-odd for solid vs hole.
 */
export const traceClosedRings = (
  polylines: readonly (readonly Point2[])[],
  tolerance: number,
): readonly (readonly Point2[])[] => {
  const index = createNodeIndex(tolerance);
  const undirected: Array<readonly [number, number]> = [];
  const seenEdges = new Set<string>();

  polylines.forEach((points) => {
    if (points.length < 2) {
      return;
    }
    const nodes: number[] = [];
    points.forEach((point) => {
      const previous = nodes[nodes.length - 1];
      const node = index.nodeFor(point);
      if (previous === undefined || node !== previous) {
        nodes.push(node);
      }
    });
    for (let i = 1; i < nodes.length; i += 1) {
      const from = nodes[i - 1];
      const to = nodes[i];
      if (from === undefined || to === undefined || from === to) {
        continue;
      }
      const key = edgeKey(from, to);
      if (seenEdges.has(key)) {
        continue;
      }
      seenEdges.add(key);
      undirected.push([from, to]);
    }
  });

  if (undirected.length === 0) {
    return [];
  }

  const nodeCount = index.positions.length;
  const incident: number[][] = Array.from({ length: nodeCount }, () => []);
  undirected.forEach((edge, edgeIndex) => {
    const start = incident[edge[0]];
    const end = incident[edge[1]];
    if (start) {
      start.push(edgeIndex);
    }
    if (end) {
      end.push(edgeIndex);
    }
  });

  const alive = new Uint8Array(undirected.length);
  alive.fill(1);
  const degreeOf = (node: number): number => {
    const edges = incident[node];
    if (!edges) {
      return 0;
    }
    let degree = 0;
    edges.forEach((edgeIndex) => {
      if (alive[edgeIndex]) {
        degree += 1;
      }
    });
    return degree;
  };

  const dangling: number[] = [];
  for (let node = 0; node < nodeCount; node += 1) {
    if (degreeOf(node) === 1) {
      dangling.push(node);
    }
  }
  while (dangling.length > 0) {
    const node = dangling.pop();
    if (node === undefined || degreeOf(node) !== 1) {
      continue;
    }
    const edges = incident[node];
    if (!edges) {
      continue;
    }
    const edgeIndex = edges.find((candidate) => alive[candidate] === 1);
    if (edgeIndex === undefined) {
      continue;
    }
    alive[edgeIndex] = 0;
    const edge = undirected[edgeIndex];
    if (!edge) {
      continue;
    }
    const other = edge[0] === node ? edge[1] : edge[0];
    if (degreeOf(other) === 1) {
      dangling.push(other);
    }
  }

  const liveEdges: Array<readonly [number, number]> = [];
  undirected.forEach((edge, edgeIndex) => {
    if (alive[edgeIndex]) {
      liveEdges.push(edge);
    }
  });

  if (liveEdges.length === 0) {
    return [];
  }

  const halfCount = liveEdges.length * 2;
  const from = new Int32Array(halfCount);
  const to = new Int32Array(halfCount);
  const angle = new Float64Array(halfCount);

  liveEdges.forEach((edge, edgeIndex) => {
    const a = edge[0];
    const b = edge[1];
    const pa = index.positions[a];
    const pb = index.positions[b];
    const he = edgeIndex * 2;
    from[he] = a;
    to[he] = b;
    from[he + 1] = b;
    to[he + 1] = a;
    angle[he] = pa && pb ? Math.atan2(pb[1] - pa[1], pb[0] - pa[0]) : 0;
    angle[he + 1] = pa && pb ? Math.atan2(pa[1] - pb[1], pa[0] - pb[0]) : 0;
  });

  const outgoing: number[][] = Array.from({ length: nodeCount }, () => []);
  for (let he = 0; he < halfCount; he += 1) {
    const origin = from[he];
    const bucket = outgoing[origin];
    if (bucket) {
      bucket.push(he);
    }
  }

  const ambiguous = new Uint8Array(nodeCount);
  outgoing.forEach((bucket, node) => {
    bucket.sort((left, right) => angle[left] - angle[right]);
    if (bucket.length < 2) {
      return;
    }
    for (let i = 0; i < bucket.length; i += 1) {
      const current = bucket[i];
      const next = bucket[(i + 1) % bucket.length];
      if (current === undefined || next === undefined) {
        continue;
      }
      let delta = angle[next] - angle[current];
      if (i === bucket.length - 1) {
        delta += 2 * Math.PI;
      }
      if (delta < ANGLE_TIE_RAD) {
        ambiguous[node] = 1;
        return;
      }
    }
  });

  const nextOf = (he: number): number | null => {
    const node = to[he];
    if (ambiguous[node]) {
      return null;
    }
    const bucket = outgoing[node];
    if (!bucket || bucket.length < 2) {
      return null;
    }
    const twin = he ^ 1;
    const twinIndex = bucket.indexOf(twin);
    if (twinIndex < 0) {
      return null;
    }
    const nextIndex = twinIndex === 0 ? bucket.length - 1 : twinIndex - 1;
    const next = bucket[nextIndex];
    if (next === undefined || next === twin) {
      return null;
    }
    return next;
  };

  const used = new Uint8Array(halfCount);
  const rings: Point2[][] = [];

  for (let start = 0; start < halfCount; start += 1) {
    if (used[start]) {
      continue;
    }
    const walk: number[] = [];
    const seen = new Set<number>();
    let he = start;
    let closed = false;

    for (;;) {
      if (seen.has(he)) {
        closed = he === start;
        break;
      }
      seen.add(he);
      walk.push(he);
      const next = nextOf(he);
      if (next === null) {
        break;
      }
      if (next === start) {
        closed = true;
        break;
      }
      if (walk.length > halfCount) {
        break;
      }
      he = next;
    }

    seen.forEach((id) => {
      used[id] = 1;
    });

    if (!closed) {
      continue;
    }

    const ring: Point2[] = [];
    walk.forEach((edge) => {
      const origin = index.positions[from[edge]];
      if (origin) {
        ring.push(origin);
      }
    });
    if (ring.length < MIN_RING_POINTS) {
      continue;
    }
    const signed = shoelaceDouble(ring);
    if (signed <= 0) {
      continue;
    }
    if (Math.abs(signed) / 2 <= MIN_FACE_AREA) {
      continue;
    }
    rings.push(rotateToMinVertex(ring));
  }

  return rings;
};
